import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { PlanCoverageInfo } from "@/model/demographics";
import { MEMBER_STATUS, MIME_TYPE } from "@/constants";
import { lowerCaseComparison } from "./string-util";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "boolean") return false;
  if (typeof value === "number") return isNaN(value);
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (value instanceof Map || value instanceof Set) return value.size === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

/**
 * Builds the "why this button is disabled" sentence for a form-submit button that stays
 * reachable (aria-disabled, not native `disabled`) instead of being removed from the tab
 * order.
 *
 * 3.3.1 Error Identification — A: names the *specific* empty field(s) rather than a
 * static list of everything the form could ever require, so a member who has already
 * filled in three of four fields hears exactly what's left, not a description of the
 * whole form. Leads with "Disabled" explicitly because aria-disabled's own announcement
 * of that state isn't consistently supported across screen reader/browser pairings.
 */
export function describeMissingFields(
  fields: Array<{ label: string; value: unknown }>,
): string {
  const missing = fields
    .filter(({ value }) => isEmpty(value))
    .map(({ label }) => label);
  if (missing.length === 0) return "";
  const list =
    missing.length === 1
      ? missing[0]
      : `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`;
  return `Disabled. Enter your ${list} to continue.`;
}

export function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  wait: number,
  immediate?: boolean,
) {
  let timeout: NodeJS.Timeout | null;

  return function (...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };

    if (immediate && !timeout) func(...args);

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function formatToCurrency(numToFormat: number) {
  if (numToFormat === null || isNaN(numToFormat)) {
    return "N/A";
  } else {
    const isNegative = numToFormat < 0;
    const absNumToFormat = Math.abs(numToFormat);
    const formattedAmount = absNumToFormat.toFixed(2);
    const result = formattedAmount.replace(/\d(?=(\d{3})+\.)/g, "$&,");
    const decimalPart = result.split(".");
    if (Number(decimalPart[1]) > 0) {
      return (isNegative ? "-$" : "$") + result;
    } else {
      return (isNegative ? "-$" : "$") + decimalPart[0];
    }
  }
}

export const hasNumberInString = (value: string) => {
  if (typeof value !== "string") return false;

  return /\d+(\.\d+)?/.test(value); // validate number in string
};

export const normalizeClaimText = (value: string) => {
  const str = String(value).trim();
  if (str === "" || str.toUpperCase() === "NA") {
    return "N/A";
  }
  // make first letter capital
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Object URLs handed to another browsing context must outlive that context's
 * read. Edge's PDF viewer keeps issuing range requests after the tab has
 * navigated, so revoking eagerly truncates the document.
 */
const BLOB_URL_TTL_MS = 120_000;

export function revokeObjectUrlLater(
  url: string,
  ttlMs = BLOB_URL_TTL_MS,
): void {
  setTimeout(() => URL.revokeObjectURL(url), ttlMs);
}

/**
 * Same-origin page that hosts the <embed> for PDFs.
 *
 * Navigating a tab directly at a blob: URL makes Chromium synthesise its own
 * host document and size it with style *attributes*. A blob: document inherits
 * the CSP of the page that minted the URL, and the deployed policy carries no
 * 'unsafe-inline' in style-src — so Edge drops that sizing and the viewer
 * collapses to the default 300x150 replaced-element box. Our own page takes its
 * sizing from an external stylesheet, which the policy already allows.
 *
 * Lives in client/public/, so it ships as-is to server/dist/public and is
 * served by express.static ahead of the SPA catch-all.
 *
 * The full fix also needs `blob:` in the nginx frame-src directive —
 *
 *   frame-src 'self' blob: https://*.zakipointhealth.com;
 *
 * because Chromium loads the PDF plugin into a guest frame that frame-src
 * governs, and `blob:` does not match `'self'`. This is NOT a deploy
 * prerequisite: pdf-viewer.js probes for the directive and falls back to the
 * plain top-level navigation when it is missing, so shipping in either order is
 * safe and the page upgrades itself once the policy changes. See CSRTB-1034.
 */
const PDF_VIEWER_PATH = "/pdf-viewer.html";

/**
 * The object URL travels in the fragment rather than the query so it never
 * reaches the server, which has no use for it. The token alongside it lets the
 * viewer ask this tab for the bytes when it cannot read the object URL back.
 */
function buildPdfViewerUrl(objectUrl: string, token: string): string {
  // Deliberately not `new URL()` — callers stub the global URL object to fake
  // createObjectURL, which takes the constructor with it.
  const params = new URLSearchParams({ url: objectUrl, token });
  return `${PDF_VIEWER_PATH}#${params.toString()}`;
}

function createViewerToken(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return (
    uuid ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  );
}

/**
 * Answers the viewer tab's request for the document bytes.
 *
 * When the browser's own PDF plugin cannot be used, the viewer renders the file
 * itself and needs the bytes. Reading the object URL back is governed by
 * connect-src, so this in-memory handover is the reliable route: it touches
 * neither the network nor storage, so no PHI is written to disk.
 *
 * The listener is dropped on the same TTL as the object URL.
 */
function serveDocumentToViewer(base64: string, token: string): void {
  function onMessage(event: MessageEvent) {
    if (event.origin !== window.location.origin) return;
    const data = event.data as { type?: string; token?: string } | undefined;
    if (data?.type !== "zc-pdf-viewer:request" || data.token !== token) return;

    const viewerWindow = event.source as Window | null;
    viewerWindow?.postMessage(
      { type: "zc-pdf-viewer:payload", token, base64 },
      window.location.origin,
    );
  }

  window.addEventListener("message", onMessage);
  setTimeout(
    () => window.removeEventListener("message", onMessage),
    BLOB_URL_TTL_MS,
  );
}

export function base64ToBlob(base64: string, mimeType: string): Blob {
  const binaryData = atob(base64.replace(/\s/g, ""));
  const length = binaryData.length;
  const array = new Uint8Array(length);
  for (let i = 0; i < length; i++) {
    array[i] = binaryData.charCodeAt(i);
  }
  return new Blob([array], { type: mimeType });
}

export function openPendingWindow(): Window | null {
  const pendingWindow = window.open("", "_blank");
  try {
    if (pendingWindow) pendingWindow.document.title = "Loading…";
  } catch {
    // The tab is still usable without it.
  }
  return pendingWindow;
}

/**
 * Opens base64 file data in a new tab. Resolves false when the file could not
 * be decoded or the popup was blocked, so callers can surface an error.
 *
 * Pass a tab from openPendingWindow when the file was fetched after the click;
 * it is navigated to the file instead of opening a new one.
 */
export async function openFileFromBase64(
  base64: string,
  mimeType: string,
  pendingWindow?: Window | null,
): Promise<boolean> {
  try {
    const blob = base64ToBlob(base64, mimeType);

    // createObjectURL returns a usable URL before Chromium has finished
    // committing a large blob to blob storage. Navigating straight to it lets
    // the new tab begin reading mid-commit: the read comes up short while
    // Content-Length already advertises the full size, so the file renders
    // half-loaded. Edge shows this on first open and then renders correctly on
    // a retry, once the blob is warm. Reading the blob back forces the commit
    // to finish before the tab can start reading it.
    if (typeof blob.arrayBuffer === "function") {
      await blob.arrayBuffer();
    }

    const url = URL.createObjectURL(blob);

    // Only PDFs go through the viewer shim; anything else has no plugin
    // document to collapse, so leave it on the plain blob navigation.
    const isPdf = mimeType === MIME_TYPE.PDF;
    const token = isPdf ? createViewerToken() : "";
    const target = isPdf ? buildPdfViewerUrl(url, token) : url;

    if (isPdf) serveDocumentToViewer(base64, token);
    let openedWindow: Window | null;
    if (pendingWindow && !pendingWindow.closed) {
      pendingWindow.location.href = target;
      openedWindow = pendingWindow;
    } else {
      // Do not pass "noopener" — it forces a null return and would make
      // every open look like a blocked popup.
      openedWindow = window.open(target, "_blank");
    }

    if (!openedWindow) {
      URL.revokeObjectURL(url);
      return false;
    }

    revokeObjectUrlLater(url);
    return true;
  } catch {
    return false;
  }
}

export function normalizeUrl(url: string): string {
  if (!/^https?:\/\//i.test(url)) {
    return `https://${url}`;
  }
  return url;
}

export function downloadBase64File(
  base64: string,
  fileName: string,
  mimeType = "application/pdf",
): boolean {
  try {
    if (!base64) return false;

    const blob = base64ToBlob(base64, mimeType);

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;

    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return true;
  } catch {
    return false;
  }
}

/**
 * Capitalizes a string safely.
 * creating this util function because some fonts does not support css text-transform: 'capitalize'
 *
 * @example
 * capitalize("termed") => "Termed"
 * capitalize("ACTIVE") => "Active"
 * capitalize("in_progress") => "In_progress"
 */
export const capitalize = (value?: string | null): string => {
  if (!value) return "";
  const normalized = value.toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const getTimezoneAbbr = (timeZone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? timeZone;
  } catch {
    return timeZone;
  }
};

export const formatHour = (hms: string): string => {
  const parts = hms.split(":");
  const h = parseInt(parts[0] ?? "", 10);
  const m = parseInt(parts[1] ?? "0", 10);
  if (Number.isNaN(h)) return hms;
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return m === 0
    ? `${hour12} ${period}`
    : `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

export const renderTemplate = (
  template: string,
  values: Record<string, string>,
): string =>
  Object.entries(values).reduce(
    (acc, [key, val]) => acc.replace(new RegExp(`\\{${key}\\}`, "g"), val),
    template,
  );

export const isOfflineNow = (details: {
  from_hour: string;
  to_hour: string;
  time_zone: string;
}): boolean => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: details.time_zone,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(new Date());

    const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
    const hour = parseInt(
      parts.find((p) => p.type === "hour")?.value ?? "0",
      10,
    );
    const minute = parseInt(
      parts.find((p) => p.type === "minute")?.value ?? "0",
      10,
    );

    if (weekday === "Sat" || weekday === "Sun") return true;

    const [fromH, fromM = 0] = details.from_hour.split(":").map(Number);
    const [toH, toM = 0] = details.to_hour.split(":").map(Number);

    const current = hour * 60 + minute;
    const from = (fromH ?? 0) * 60 + fromM;
    const to = (toH ?? 0) * 60 + toM;

    return current < from || current >= to;
  } catch {
    return true;
  }
};

export const isCoverageActive = (coverage?: PlanCoverageInfo): boolean => {
  const status = coverage?.status;
  return lowerCaseComparison(status, MEMBER_STATUS?.ACTIVE);
};

export const isCoverageActiveOrExtended = (
  coverage?: PlanCoverageInfo,
): boolean => {
  const status = coverage?.status?.toLowerCase();

  const dateStr = coverage?.extendedTermDate;
  let hasExtendedAccess = false;

  if (dateStr) {
    const time = new Date(dateStr).getTime();
    if (!isNaN(time)) {
      hasExtendedAccess = Date.now() < time;
    }
  }

  const isTermedWithExtendedAccess =
    status === MEMBER_STATUS?.TERMED?.toLowerCase() && hasExtendedAccess;

  return isCoverageActive(coverage) || isTermedWithExtendedAccess;
};
